/**
 * Bakes every glyph in `assets/glyphs.ts` into one `THREE.DataTexture` at
 * module load. No fetch, no canvas, no image decode — the art is source code,
 * so the atlas is ready before the first frame.
 *
 * Channel layout, and the reason it exists:
 *
 *   R  fill      the drawn mass
 *   G  outline   a one-pixel dilation ring around the mass, plus any `-` the
 *                art authored directly (hollow bodies need a stated core)
 *   B  detail    eyes, seams, cores — the `+` pixels
 *   A  unused    held at 255
 *
 * Keeping fill and outline apart is what lets a single 16×16 drawing serve all
 * eight enemy colours. The shader tints R with the enemy's phosphor at HDR
 * intensity and G with a near-black variant of the same hue, so the sprite cuts
 * a hard hole in the glowing lattice behind it instead of dissolving into it.
 * Bake the outline into the fill instead and every enemy needs its own texture.
 *
 * Each glyph is padded by one pixel on all four sides so the dilated outline
 * lives inside its own rect, then separated from its neighbours by a further
 * one-pixel gutter. `rect()` returns the padded box, so a "16×16" glyph draws
 * as an 18×18 quad and arrives on screen with its rim intact.
 */

import * as THREE from 'three';
import { GLYPHS, GLYPH_NAMES, validateGlyphs, type GlyphName } from '@/assets/glyphs';

/** Pixel geometry of the bake. */
export const SIZE = {
  /** Border reserved inside each rect for the derived outline ring. */
  pad: 1,
  /** Dead pixels between neighbouring rects. */
  gutter: 1,
  /** Nominal art sizes. Quads are these plus `2 * pad`. */
  small: 8,
  base: 16,
  boss: 32,
} as const;

/** A packed glyph, in atlas pixels and in UV. */
export interface GlyphRect {
  readonly name: GlyphName;
  readonly index: number;
  /** Atlas pixel origin of the padded box. */
  readonly x: number;
  readonly y: number;
  /** Padded box extent, i.e. the on-screen quad size. */
  readonly w: number;
  readonly h: number;
  /** Unpadded art size, i.e. the logical glyph edge. */
  readonly art: number;
  readonly u0: number;
  readonly v0: number;
  readonly u1: number;
  readonly v1: number;
}

// ─────────────────────────────────────────────────────────────── packing ──

validateGlyphs();

interface Cell {
  name: GlyphName;
  art: number;
  w: number;
  h: number;
  x: number;
  y: number;
}

const cells: Cell[] = [];
for (let i = 0; i < GLYPH_NAMES.length; i++) {
  const name = GLYPH_NAMES[i];
  const art = GLYPHS[name].size;
  const edge = art + SIZE.pad * 2;
  cells.push({ name, art, w: edge, h: edge, x: 0, y: 0 });
}

// Tallest first. With a handful of discrete sizes a shelf packer is optimal
// enough and, unlike a guillotine tree, it stays readable.
cells.sort((a, b) => (b.h - a.h) || (b.w - a.w) || (a.name < b.name ? -1 : 1));

/** Lays the shelves out at a candidate width. Returns used height, or -1. */
function shelfPack(edge: number): number {
  let shelfY = 0;
  let shelfH = 0;
  let penX = 0;
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    const advance = c.w + SIZE.gutter;
    if (penX + c.w > edge) {
      shelfY += shelfH + SIZE.gutter;
      shelfH = 0;
      penX = 0;
    }
    if (c.w > edge || shelfY + c.h > edge) return -1;
    c.x = penX;
    c.y = shelfY;
    penX += advance;
    if (c.h > shelfH) shelfH = c.h;
  }
  return shelfY + shelfH;
}

const CANDIDATES = [64, 128, 256, 512, 1024] as const;

let chosen = 0;
for (let i = 0; i < CANDIDATES.length; i++) {
  if (shelfPack(CANDIDATES[i]) >= 0) {
    chosen = CANDIDATES[i];
    break;
  }
}
if (chosen === 0) {
  throw new Error('GlyphAtlas: glyph set does not fit a 1024² power-of-two atlas');
}

/** Power-of-two edge of the baked texture. */
export const ATLAS_SIZE = chosen;

// ────────────────────────────────────────────────────────────────── bake ──

const data = new Uint8Array(ATLAS_SIZE * ATLAS_SIZE * 4);
for (let i = 3; i < data.length; i += 4) data[i] = 255;

/** `.` `#` `+` lookup that treats everything outside the art as empty. */
function at(rows: readonly string[], size: number, x: number, y: number): string {
  if (x < 0 || y < 0 || x >= size || y >= size) return '.';
  return rows[y][x];
}

const rects: GlyphRect[] = [];
const byName = new Map<GlyphName, GlyphRect>();

/** Flat `vec4(x, y, w, h)` in atlas pixels, uploaded as a shader uniform array. */
export const RECT_PX = new Float32Array(cells.length * 4);

for (let i = 0; i < cells.length; i++) {
  const c = cells[i];
  const g = GLYPHS[c.name];
  const rows = g.rows;

  for (let py = 0; py < c.h; py++) {
    for (let px = 0; px < c.w; px++) {
      // Art coordinates run from -pad to art+pad; the ring outside the drawing
      // is where the dilated outline lands.
      const ax = px - SIZE.pad;
      const ay = py - SIZE.pad;
      const ch = at(rows, c.art, ax, ay);
      const fill = ch === '#' ? 255 : 0;
      const detail = ch === '+' ? 255 : 0;

      // `-` is an authored dark pixel. Dilation cannot reach the middle of a
      // ring, so a hollow body has to state its own core or the lattice shines
      // straight through it.
      let outline = ch === '-' ? 255 : 0;
      if (fill === 0 && detail === 0 && outline === 0) {
        // Eight-neighbour dilation. Four-neighbour leaves diagonal staircases
        // with a gap at every step, which shows up as a dotted rim.
        for (let oy = -1; oy <= 1 && outline === 0; oy++) {
          for (let ox = -1; ox <= 1; ox++) {
            if (ox === 0 && oy === 0) continue;
            const n = at(rows, c.art, ax + ox, ay + oy);
            if (n === '#' || n === '+') { outline = 255; break; }
          }
        }
      }

      const o = ((c.y + py) * ATLAS_SIZE + (c.x + px)) * 4;
      data[o] = fill;
      data[o + 1] = outline;
      data[o + 2] = detail;
    }
  }

  const rect: GlyphRect = {
    name: c.name,
    index: i,
    x: c.x,
    y: c.y,
    w: c.w,
    h: c.h,
    art: c.art,
    u0: c.x / ATLAS_SIZE,
    v0: c.y / ATLAS_SIZE,
    u1: (c.x + c.w) / ATLAS_SIZE,
    v1: (c.y + c.h) / ATLAS_SIZE,
  };
  rects.push(rect);
  byName.set(c.name, rect);

  RECT_PX[i * 4] = c.x;
  RECT_PX[i * 4 + 1] = c.y;
  RECT_PX[i * 4 + 2] = c.w;
  RECT_PX[i * 4 + 3] = c.h;
}

/** How many rects the shader's uniform array must hold. */
export const RECT_COUNT = rects.length;

// ─────────────────────────────────────────────────────────────── texture ──

/**
 * `flipY = false` so atlas row 0 is the glyph's top row and v grows downward,
 * matching buffer space. `NoColorSpace` because these are coverage masks, not
 * colour — an sRGB decode here would eat the outline.
 */
export const texture = new THREE.DataTexture(
  data,
  ATLAS_SIZE,
  ATLAS_SIZE,
  THREE.RGBAFormat,
  THREE.UnsignedByteType,
);
texture.magFilter = THREE.NearestFilter;
texture.minFilter = THREE.NearestFilter;
texture.generateMipmaps = false;
texture.wrapS = THREE.ClampToEdgeWrapping;
texture.wrapT = THREE.ClampToEdgeWrapping;
texture.colorSpace = THREE.NoColorSpace;
texture.flipY = false;
texture.premultiplyAlpha = false;
texture.unpackAlignment = 4;
texture.needsUpdate = true;
texture.name = 'glyph-atlas';

// ──────────────────────────────────────────────────────────────── lookup ──

/** Packed box for a glyph. Throws rather than silently drawing the wrong art. */
export function rect(name: GlyphName): GlyphRect {
  const r = byName.get(name);
  if (r === undefined) throw new Error(`GlyphAtlas: unknown glyph "${name}"`);
  return r;
}

/** Index into `RECT_PX` / the shader's `uRects`. */
export function rectIndex(name: GlyphName): number {
  return rect(name).index;
}

/** Every rect, in packing order. Index `i` matches `RECT_PX[i * 4 .. i * 4 + 3]`. */
export function allRects(): readonly GlyphRect[] {
  return rects;
}

/** Raw mask bytes. Exposed for tests; do not mutate. */
export function atlasBytes(): Uint8Array {
  return data;
}

/**
 * Releases the GPU texture. The atlas is a module singleton shared by every
 * consumer, so a renderer's own `dispose()` must not call this — only teardown
 * of the whole app should.
 */
export function disposeAtlas(): void {
  texture.dispose();
}
